import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { Sparkles } from "lucide-react";
import { SIGN_GLYPH, PLANET_GLYPH, decanOf, stelliumsOf, placementsFrom } from "../lib/astro";

const SECONDARY = ["Mercury", "Venus", "Mars", "Jupiter", "Saturn", "Uranus", "Neptune", "Pluto"];
const ROMAN = ["I", "II", "III"];

function DecanTag({ decan, tone = "gold" }) {
  if (!decan) return null;
  const color = tone === "violet" ? "text-accent" : "text-primary";
  return (
    <span className={`font-mono text-[10px] uppercase tracking-[0.2em] ${color}`}>
      {decan.ordinal} dec · {decan.subRuler}
    </span>
  );
}

const CARD =
  "w-full rounded-lg border border-primary/15 bg-white/[0.03] backdrop-blur-xl p-6 flex flex-col min-h-[320px]";

function TrinityCard({ rows, interactive, onTap }) {
  return (
    <div className={CARD} data-testid="astro-card-trinity" aria-label="The Trinity: Sun, Moon and Rising">
      <div className="mb-5">
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-accent/90">I · The Trinity</p>
        <h3 className="font-serif text-3xl font-light tracking-tight mt-1">Core identity</h3>
      </div>
      <div className="flex-1 flex flex-col justify-center">
        {rows.map((r) => {
          const decan = decanOf(r.sign, r.degree);
          return (
            <button
              key={r.planet}
              data-testid={`trinity-${r.planet.toLowerCase()}-row`}
              disabled={!interactive || !r.sign}
              onClick={() => onTap(r.planet, r.sign, r.degree)}
              className="group flex items-center justify-between gap-4 py-4 border-b border-border/40 last:border-0 transition-all duration-300 ease-out hover:pl-2 hover:border-l-2 hover:border-l-primary text-left"
            >
              <span className="flex items-center gap-3">
                <span className="font-serif text-4xl text-primary leading-none w-10 text-center">{r.glyph}</span>
                <span className="font-serif text-xl font-medium">{r.planet}</span>
              </span>
              <span className="flex flex-col items-end">
                <DecanTag decan={r.sign ? decan : null} />
                <span className="font-serif text-lg font-light italic text-muted-foreground group-hover:text-foreground transition-colors">
                  {r.sign || "—"}
                </span>
              </span>
            </button>
          );
        })}
      </div>
      {interactive && (
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground/70 mt-4">
          Tap a luminary for a reading
        </p>
      )}
    </div>
  );
}

function StelliumCard({ stelliums, interactive, onTap }) {
  const hero = stelliums[0];
  const clickable = interactive && hero;
  return (
    <button
      className={`${CARD} relative overflow-hidden text-left ${clickable ? "hover:border-accent/40 transition-colors" : ""}`}
      data-testid="stellium-card"
      aria-label="Stelliums"
      disabled={!clickable}
      onClick={() => clickable && onTap(hero.sign, hero.planets, hero.element)}
    >
      <div className="mb-5 relative z-10">
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-accent/90">II · Concentrated Energy</p>
        <h3 className="font-serif text-3xl font-light tracking-tight mt-1">{hero ? "Stellium" : "Balance"}</h3>
      </div>

      {hero ? (
        <>
          <div className="absolute -right-8 top-16 h-40 w-40 rounded-full bg-accent/15 blur-3xl pointer-events-none" />
          <div className="flex-1 flex flex-col justify-center relative z-10">
            <div className="flex items-baseline gap-3 mb-1">
              <span className="font-serif text-5xl text-accent leading-none">{SIGN_GLYPH[hero.sign]}</span>
              <span className="font-serif text-4xl font-light">{hero.sign}</span>
            </div>
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-5">
              {hero.element} · {hero.planets.length} planets aligned
            </p>
            <div className="flex items-center" data-testid="stellium-cluster">
              <div className="absolute left-0 right-8 h-px bg-primary/20" />
              {hero.planets.map((p, i) => (
                <span
                  key={p}
                  title={p}
                  style={{ marginLeft: i === 0 ? 0 : -8 }}
                  className="relative h-11 w-11 rounded-full border border-primary/25 bg-[#0A0A0F] backdrop-blur-md text-primary text-lg font-serif inline-flex items-center justify-center"
                >
                  {PLANET_GLYPH[p] || p[0]}
                </span>
              ))}
            </div>
            {stelliums.length > 1 && (
              <p className="text-sm text-muted-foreground mt-5">
                Also concentrated in{" "}
                <span className="text-foreground italic font-serif">
                  {stelliums.slice(1).map((s) => `${s.sign} (${s.planets.length})`).join(", ")}
                </span>
                .
              </p>
            )}
            {clickable && (
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-accent/80 mt-5 flex items-center gap-1.5">
                <Sparkles size={11} /> Tap for your stellium reading
              </p>
            )}
          </div>
        </>
      ) : (
        <div className="flex-1 flex flex-col justify-center">
          <span className="font-serif text-5xl text-primary/70 leading-none mb-4">✳</span>
          <p className="font-serif text-lg font-light italic text-muted-foreground leading-relaxed">
            No single sign dominates — your planetary energy is spread evenly across the wheel, giving you range
            rather than a single point of intensity.
          </p>
        </div>
      )}
    </button>
  );
}

function AlignmentsCard({ placements, interactive, onTap }) {
  const rows = SECONDARY.map((p) => placements.find((x) => x.planet === p)).filter((p) => p && p.sign);
  return (
    <div className={CARD} data-testid="astro-card-alignments" aria-label="Planetary alignments">
      <div className="mb-4">
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-accent/90">III · Planetary Alignments</p>
        <h3 className="font-serif text-3xl font-light tracking-tight mt-1">The wider sky</h3>
      </div>
      <div className="flex-1 flex flex-col justify-center">
        {rows.map((p) => {
          const decan = decanOf(p.sign, p.degree);
          return (
            <button
              key={p.planet}
              data-testid={`alignment-${p.planet.toLowerCase()}-row`}
              disabled={!interactive}
              onClick={() => onTap(p.planet, p.sign, p.degree)}
              className="group flex items-center justify-between gap-3 py-2.5 border-b border-border/40 last:border-0 transition-all duration-300 ease-out hover:pl-1.5 hover:border-l-2 hover:border-l-primary text-left"
            >
              <span className="flex items-center gap-3 text-sm text-muted-foreground">
                <span className="font-serif text-lg text-primary w-6 text-center">{PLANET_GLYPH[p.planet]}</span>
                {p.planet}
              </span>
              <span className="flex items-center gap-3">
                <span className="font-serif italic text-foreground group-hover:text-primary transition-colors">{p.sign}</span>
                <span className="font-mono text-[9px] uppercase tracking-[0.15em] text-accent/80 w-4 text-right">
                  {decan ? ROMAN[decan.num - 1] : ""}
                </span>
                {interactive && <Sparkles size={11} className="opacity-0 group-hover:opacity-100 text-primary" />}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function AstrologySection({ natal, interactive, onExplainPlacement, onExplainStellium }) {
  const n = natal || {};
  const placements = placementsFrom(n);
  const byPlanet = Object.fromEntries(placements.map((p) => [p.planet, p]));
  const stelliums = stelliumsOf(n);

  const trinity = [
    { planet: "Sun", glyph: "☉", sign: n.sun_sign, degree: n.sun_degree ?? byPlanet.Sun?.degree },
    { planet: "Moon", glyph: "☽", sign: n.moon_sign, degree: n.moon_degree ?? byPlanet.Moon?.degree },
    { planet: "Rising", glyph: "↑", sign: n.rising_sign, degree: n.rising_degree },
  ];

  const tap = (planet, sign, degree) => {
    if (!interactive || !sign) return;
    const house = planet === "Rising" ? null : byPlanet[planet]?.house ?? null;
    onExplainPlacement(planet, sign, house, degree ? parseFloat(degree) : null);
  };

  const scroller = useRef(null);
  const cardRefs = useRef([]);
  const [active, setActive] = useState(0);
  const total = 3;

  const go = (i) => {
    const idx = Math.max(0, Math.min(total - 1, i));
    const el = cardRefs.current[idx];
    const s = scroller.current;
    if (el && s) {
      s.scrollTo({ left: el.offsetLeft - (s.clientWidth - el.clientWidth) / 2, behavior: "smooth" });
    }
    setActive(idx);
  };

  const onScroll = () => {
    const s = scroller.current;
    if (!s) return;
    const center = s.scrollLeft + s.clientWidth / 2;
    let best = 0, bestD = Infinity;
    cardRefs.current.forEach((el, i) => {
      if (!el) return;
      const c = el.offsetLeft + el.offsetWidth / 2;
      const d = Math.abs(c - center);
      if (d < bestD) { bestD = d; best = i; }
    });
    setActive(best);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      aria-roledescription="carousel"
    >
      <div className="flex items-center justify-between mb-4">
        <p className="overline">Natal Astrology</p>
        <div className="flex items-center gap-3">
          <span className="font-mono text-[11px] tracking-[0.2em] text-muted-foreground" data-testid="carousel-indicator">
            {ROMAN[active]} <span className="text-muted-foreground/40">/ III</span>
          </span>
          <button
            data-testid="carousel-prev-btn"
            onClick={() => go(active - 1)}
            disabled={active === 0}
            className="font-mono text-sm h-7 w-7 rounded-full border border-border text-muted-foreground hover:text-primary hover:border-primary/50 disabled:opacity-30 transition-colors"
            aria-label="Previous card"
          >
            ‹
          </button>
          <button
            data-testid="carousel-next-btn"
            onClick={() => go(active + 1)}
            disabled={active === total - 1}
            className="font-mono text-sm h-7 w-7 rounded-full border border-border text-muted-foreground hover:text-primary hover:border-primary/50 disabled:opacity-30 transition-colors"
            aria-label="Next card"
          >
            ›
          </button>
        </div>
      </div>

      <div
        ref={scroller}
        onScroll={onScroll}
        data-testid="astrology-carousel-container"
        className="flex gap-4 overflow-x-auto snap-x snap-mandatory -mx-1 px-1 pb-1 [&::-webkit-scrollbar]:hidden"
        style={{ scrollbarWidth: "none" }}
      >
        <div ref={(el) => (cardRefs.current[0] = el)} className="flex snap-center shrink-0 w-[86%]">
          <TrinityCard rows={trinity} interactive={interactive} onTap={tap} />
        </div>
        <div ref={(el) => (cardRefs.current[1] = el)} className="flex snap-center shrink-0 w-[86%]">
          <StelliumCard stelliums={stelliums} interactive={interactive} onTap={onExplainStellium} />
        </div>
        <div ref={(el) => (cardRefs.current[2] = el)} className="flex snap-center shrink-0 w-[86%]">
          <AlignmentsCard placements={placements} interactive={interactive} onTap={tap} />
        </div>
      </div>
    </motion.div>
  );
}
